/*
 *******************************************************************************
 *
 *  Filename:   @/contexts/shared/actions.js
 *
 *  Syntax:     NA
 *
 *  Synopsis:   Actions for contexts.
 *
 *  Author:     Norman J. Nolasco [ Archetact Software ]
 *
 *  Created:    Saturday, August 19, 2023 - 10:00 PM (CST)
 *
 *  Notes:
 *
 *  Revisions:
 *              2023/08/19  NJN Initial Build
 *
 *  Copyright (c) 2023 - Archetact Software - All Rights Reserved.
 *  Unauthorized copying of this file via any medium is strictly prohibited.
 *  Proprietary and Confidential.
 *
 *******************************************************************************
 */
import { fetchApi } from "@/utils/fetchApi";
import { setSession } from "@/utils/jwt";
import { INITIALIZE, SIGN_IN, SIGN_OUT, SIGN_UP, SAVE_EMAIL, NEW_PASSWORD } from "./constants";

export const signIn = async (email, password, dispatch) => {
  const response = await fetchApi("users", "loginUser", {
    username: email,
    password,
  });

  if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
    const result = {
      isAuthenticated: false,
      isInitialized: false,
      user: null,
      newPasswordRequired: true,
      session: response.Session,
      challengeParameters: response.ChallengeParameters,
    };
    dispatch({ type: NEW_PASSWORD, payload: result });
    return result;
  }

  if (response.error) {
    if (response.error.type === "PasswordResetRequiredException") {
      return {
        isAuthenticated: false,
        isInitialized: false,
        user: null,
        newPasswordRequired: false,
        resetPasswordRequired: true,
      };
    }
    if (response.error.type === "NotAuthorizedException") {
      return {
        isAuthenticated: false,
        isInitialized: false,
        user: null,
        newPasswordRequired: false,
      };
    }
  }

  const { authenticationResult, userResult } = response;
  const user = userResult;

  setSession(authenticationResult);
  dispatch({
    type: INITIALIZE,
    payload: {
      isAuthenticated: true,
      isInitialized: true,
      authToken: authenticationResult,
      user,
    },
  });
  dispatch({
    type: SIGN_IN,
    payload: {
      authenticationResult,
      authToken: authenticationResult,
      user,
    },
  });
  return {
    isAuthenticated: true,
    isInitialized: true,
    authToken: authenticationResult,
    user,
    newPasswordRequired: false,
  };
};

export const signOut = async (dispatch) => {
  setSession(null);
  dispatch({ type: SIGN_OUT });
  return {};
};

export const signUp = async (firstname, lastname, company, email, phone, password, dispatch) => {
  const response = await fetchApi("users", "signUpUser", {
    username: email,
    password,
    firstname,
    lastname,
    company,
    location: "Default Location",
    phone: "+" + phone.replace(/[^\d]/g, "").substr(1),
  });
  console.log("signUp response", response);
  return response;
};

export const resetPassword = async (email, dispatch) => {
  const response = await fetchApi("users", "resetPassword", {
    username: email,
  });
  dispatch({
    type: SAVE_EMAIL,
    payload: {
      email,
    },
  });
  return response;
};

export const completeResetPassword = async (email, newPassword, confirmationCode, dispatch) => {
  const response = await fetchApi("users", "resetPasswordVerify", {
    username: email,
    newPassword: newPassword,
    confirmationCode: confirmationCode,
  });
  return response;
};

export const completeNewPasswordChallenge = async (email, password, session, dispatch) => {
  const response = await fetchApi("users", "challengeNewPassword", {
    username: email,
    password: password,
    session: session,
  });

  console.log("completeNewPasswordChallenge response", response);

  const { authenticationResult, userResult } = response;
  const user = userResult;

  setSession(authenticationResult);
  dispatch({
    type: INITIALIZE,
    payload: {
      isAuthenticated: true,
      isInitialized: true,
      authToken: authenticationResult,
      user,
    },
  });
  dispatch({
    type: SIGN_IN,
    payload: {
      authToken: authenticationResult,
      user,
    },
  });
  return {
    isAuthenticated: true,
    isInitialized: true,
    authToken: authenticationResult,
    user,
    newPasswordRequired: false,
  };
};

export const confirmSignUp = async (email, confirmationCode, dispatch) => {
  const response = await fetchApi("users", "confirmSignUp", {
    username: email,
    confirmationCode: confirmationCode,
  });
  return response;
};

export const resendConfirmationCode = async (email, dispatch) => {
  const response = await fetchApi("users", "confirmSignUpResend", {
    username: email,
  });
  return response;
};
